import React, { lazy, Suspense, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { operatorConfig } from "../../utils/OperatorConfig";
import { useOperator } from "../../utils/OperatorContext";
import PrimaryButton from "../shared/ui/buttons/PrimaryButton";
import AllInOneActions from "./modules/all/AllInOneActions";
import OperatorMultiTVComponent from "./OperatorMultiTvComponent";
import WallTimerControls from "./modules/global/WallTimerControls";

/* ---------- Lazy Panels ---------- */
const DAY_PANELS = {
  GLOBAL: lazy(() => import("./modules/global/globalOperatorPanel")),
  // CHALLENGS: lazy(() =>
  //   import("./modules/global/globalOperatorCreateChalengs")
  // ),
  "Day-2": lazy(() => import("./modules/day-2/OperatorDay2Panel")),
  "Day-3": lazy(() => import("./modules/day-3/operatorDay3Panel")),
  "Day-4": lazy(() => import("./modules/day-4/day4OperatorPanelComponent")),
  "Day-6": lazy(() => import("./modules/day-6/day6OperatorPanelComponent")),
  "Day-7": lazy(() => import("./modules/day-7/day7OperatorPanelComponent")),
};

const toActionKey = (dayId) => (dayId || "").replace(/^Day-(\d+)$/i, "day$1");

/* ---------- Optional labels for nicer toasts ---------- */
const NOTIF_LABELS = {
  change_captain_pending2: "در انتظار تغییر کاپیتان",
  change_captain_started: "تغییر کاپیتان آغاز شد",
};

/* ---------- Small UI helpers (minimal / reusable) ---------- */
function SectionCard({
  title,
  accent = "from-slate-800 to-slate-800",
  children,
  right,
}) {
  return (
    <section
      className="
        rounded-2xl border border-slate-700/70
        bg-gradient-to-b from-slate-800/70 to-slate-900/40
        backdrop-blur-md shadow-sm"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-700/60">
        <div className="flex items-center gap-3">
          <span className={`inline-block h-5 w-1 rounded bg-emerald-500`} />
          <h3 className="text-slate-100 text-lg font-semibold">{title}</h3>
        </div>
        {right}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function SoftCountBadge({ children }) {
  return (
    <span className="rounded-full bg-slate-700/60 text-slate-300 px-2.5 py-0.5 text-xs">
      {children}
    </span>
  );
}

export default function OperatorComponent({ socketData }) {
  const { selectedDay, updateDay, dispatchAction } = useOperator();

  const actionButtons =
    operatorConfig?.actions?.[toActionKey(selectedDay)] ?? [];
  const Panel = DAY_PANELS[selectedDay];
  // const Challengs = DAY_PANELS["CHALLENGS"]
  const selectedDayMeta = operatorConfig.days.find((d) => d.id === selectedDay);

  /* ---------- Notifications from socket (no duplicates) ---------- */
  useEffect(() => {
    const notifications = socketData?.data?.notification;
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    const { challenge_id, stage_id } = socketData.data;

    notifications.forEach((n) => {
      const label = NOTIF_LABELS[n] ?? n.replace(/_/g, " ");
      const id = `notif:${n}:${challenge_id ?? "na"}:${stage_id ?? "na"}`; // unique toastId

      toast(label, {
        toastId: id, // prevents duplicate toasts
        autoClose: false, // persist until user closes
        closeOnClick: true,
        draggable: true,
      });
    });
  }, [socketData]);

  return (
    <div
    
    className="min-h-screen bg-slate-950">
      {/* Toasts (remove if already placed globally) */}
      {/* <OperatorMultiTVComponent /> */}
      <ToastContainer position="top-right" theme="dark" newestOnTop />

      {/* Header Bar */}
      <div className="sticky top-0 z-10 border-b border-slate-800/70 bg-slate-950/70 backdrop-blur supports-[backdrop-filter]:bg-slate-950/50">
        <div className="mx-auto max-w-7xl px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-100">
                Operator Dashboard
              </h1>
              <p className="text-slate-400 text-sm mt-0.5">
                مدیریت و کنترل رویدادهای روزانه
              </p>
            </div>
            {selectedDay && (
              <div className="hidden md:flex items-center gap-2 text-sm text-slate-300">
                <span className="text-slate-400">روز انتخاب‌شده:</span>
                <SoftCountBadge>
                  {selectedDayMeta?.label ?? selectedDay}
                </SoftCountBadge>
              </div>
            )}
          </div>
        </div>
      </div>

      <main
    
      className="mx-auto  px-5 py-6 space-y-6">
        {/* NEW: Collapsible All-in-one Actions (collapsed by default) */}

        {/* Day Selector */}
        <SectionCard
          title="انتخاب روز"
          right={
            selectedDay ? (
              <span className="text-xs text-slate-400">
                انتخاب‌شده:{" "}
                <span className="text-slate-200">
                  {selectedDayMeta?.label ?? selectedDay}
                </span>
              </span>
            ) : null
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2.5 text-[50px] ">
            {operatorConfig.days.map((d) => {
              const isActive = selectedDay === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => updateDay(d.id)}
                  className={[
                    "group w-full rounded-xl px-3 py-2.5 text-[40px] font-medium transition",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60",
                    isActive
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "bg-slate-800/70 text-slate-300 hover:bg-slate-800 hover:shadow-sm",
                  ].join(" ")}
                  aria-pressed={isActive}
                >
                  <div className="flex items-center justify-between">
                    <span>{d.label}</span>
                    {isActive && <span className="i-lucide-check h-4 w-4" />}
                  </div>
                </button>
              );
            })}
          </div>
        </SectionCard>

        {/* Actions */}
        {!!actionButtons.length && (
          <SectionCard
            title="اکشن‌ها"
            right={<SoftCountBadge>{actionButtons.length}</SoftCountBadge>}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 text-[50px]">
              {actionButtons.map((a) => (
                <PrimaryButton
                  key={a.id}
                  text={a.label}
                  onClick={() => dispatchAction(a.id)}
                  className="
                    w-full rounded-xl
                    bg-indigo-600 text-white
                    hover:bg-indigo-600/90
                    focus-visible:ring-2 focus-visible:ring-indigo-500/60
                    transition
                  "
                />
              ))}
            </div>
          </SectionCard>
        )}
        <AllInOneActions
          operatorConfig={operatorConfig}
          selectedDay={selectedDay}
          updateDay={updateDay}
          dispatchAction={dispatchAction}
          toActionKey={toActionKey}
        />
      <WallTimerControls />

        {/* <SectionCard title={"وارد کردن عنوان چالش"}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center gap-3 text-slate-300">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-500" />
                  <span>در حال بارگذاری پنل…</span>
                </div>
              </div>
            }
          >
            <Challengs />
          </Suspense>
        </SectionCard> */}
        {/* Day Panel */}
        {Panel && (
          <SectionCard
            title={`${
              selectedDayMeta?.label ?? selectedDay
            } — کنترل‌های سوال و جواب`}
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-16">
                  <div className="flex items-center gap-3 text-slate-300">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-500" />
                    <span>در حال بارگذاری پنل…</span>
                  </div>
                </div>
              }
            >
              <Panel />
            </Suspense>
          </SectionCard>
        )}
      </main>
    </div>
  );
}
